import { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";

const PREFERRED_COLUMNS = ["source_filename", "Date", "Amount", "Description"];

const formatCellValue = (key, value) => {
	if (value == null || value === "") return "-";
	if (key === "Amount" && typeof value === "number") {
		return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
	}
	return String(value);
};

const normalizePreviewResponse = (previewData) => {
	if (previewData == null) return null;

	const result = previewData.result ?? previewData;
	const data = Array.isArray(result?.data) ? result.data : [];

	const columnKeys = data.reduce((keys, row) => {
		Object.keys(row || {}).forEach((key) => {
			if (!keys.includes(key)) keys.push(key);
		});
		return keys;
	}, []);

	const orderedColumns = [
		...PREFERRED_COLUMNS.filter((key) => columnKeys.includes(key)),
		...columnKeys.filter((key) => !PREFERRED_COLUMNS.includes(key)),
	];

	return {
		status: previewData.status ?? "Unknown",
		message: previewData.message ?? "",
		rowCount: result?.row_count ?? data.length,
		filesUploaded: result?.files_uploaded ?? 0,
		filesProcessed: result?.files_processed ?? 0,
		filesFailed: result?.files_failed ?? 0,
		errors: Array.isArray(result?.errors) ? result.errors : [],
		data,
		columns: orderedColumns,
		isSuccess: String(previewData.status ?? "").toLowerCase() === "success",
	};
};

const PayByLinkPreviewDialog = ({ open, onClose, previewData, images = [], onApprove, approving }) => {
	const preview = useMemo(() => normalizePreviewResponse(previewData), [previewData]);
	const [selectedIndexes, setSelectedIndexes] = useState(() => new Set());

	const imagePreviews = useMemo(
		() =>
			images.map((file) => ({
				file,
				url: URL.createObjectURL(file),
			})),
		[images]
	);

	useEffect(() => {
		return () => {
			imagePreviews.forEach(({ url }) => URL.revokeObjectURL(url));
		};
	}, [imagePreviews]);

	// Default: select all rows whenever a new preview opens / loads.
	useEffect(() => {
		if (!open || !preview?.data?.length) {
			setSelectedIndexes(new Set());
			return;
		}
		setSelectedIndexes(new Set(preview.data.map((_, index) => index)));
	}, [open, preview]);

	const rowCount = preview?.data?.length ?? 0;
	const selectedCount = selectedIndexes.size;
	const allSelected = rowCount > 0 && selectedCount === rowCount;
	const someSelected = selectedCount > 0 && selectedCount < rowCount;

	const canApprove =
		preview != null &&
		preview.isSuccess &&
		preview.filesFailed === 0 &&
		selectedCount > 0;

	const handleToggleAll = () => {
		if (!preview?.data?.length) return;
		if (allSelected) {
			setSelectedIndexes(new Set());
			return;
		}
		setSelectedIndexes(new Set(preview.data.map((_, index) => index)));
	};

	const handleToggleRow = (rowIndex) => {
		setSelectedIndexes((prev) => {
			const next = new Set(prev);
			if (next.has(rowIndex)) next.delete(rowIndex);
			else next.add(rowIndex);
			return next;
		});
	};

	const handleApprove = () => {
		if (!preview?.data?.length || !canApprove) return;
		const selectedRows = preview.data.filter((_, index) => selectedIndexes.has(index));
		onApprove?.(selectedRows);
	};

	return (
		<Dialog open={open} onClose={approving ? undefined : onClose} fullWidth maxWidth="lg">
			<DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pr: 1.5 }}>
				<Box>
					<Typography variant="h6" fontWeight={600}>
						Review Pay By Link data
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						Select the rows you want to keep. Only checked records will be saved.
					</Typography>
				</Box>
				<IconButton onClick={onClose} disabled={approving} aria-label="Close">
					<CloseRoundedIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
				{preview && (
					<Alert severity={preview.isSuccess ? "success" : "error"} sx={{ borderRadius: 1.5 }}>
						<Typography variant="subtitle2" fontWeight={600}>
							{preview.status}
						</Typography>
						{preview.message && (
							<Typography variant="body2" sx={{ mt: 0.25 }}>
								{preview.message}
							</Typography>
						)}
					</Alert>
				)}

				{preview && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
						<Chip label={`${preview.rowCount} rows`} size="small" variant="outlined" />
						<Chip
							label={`${selectedCount} selected`}
							size="small"
							variant="outlined"
							color={selectedCount > 0 ? "primary" : "default"}
						/>
						<Chip label={`${preview.filesUploaded} uploaded`} size="small" variant="outlined" />
						<Chip label={`${preview.filesProcessed} processed`} size="small" variant="outlined" color="success" />
						{preview.filesFailed > 0 && (
							<Chip label={`${preview.filesFailed} failed`} size="small" variant="outlined" color="error" />
						)}
						<Chip label="Preview" size="small" variant="outlined" color="warning" />
					</Box>
				)}

				{preview?.errors.length > 0 && (
					<Alert severity="error" sx={{ borderRadius: 1.5 }}>
						{preview.errors.map((error, index) => (
							<Typography key={index} variant="body2">
								{typeof error === "string" ? error : JSON.stringify(error)}
							</Typography>
						))}
					</Alert>
				)}

				{imagePreviews.length > 0 && (
					<Box>
						<Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
							Uploaded images ({imagePreviews.length})
						</Typography>
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
							{imagePreviews.map(({ file, url }) => (
								<Paper
									key={`${file.name}-${file.lastModified}`}
									elevation={0}
									sx={{
										width: 96,
										borderRadius: 1.5,
										border: "1px solid",
										borderColor: "divider",
										overflow: "hidden",
									}}>
									<Box
										component="img"
										src={url}
										alt={file.name.replace(/^PAY_BY_LINK-/, "")}
										sx={{ width: "100%", height: 72, objectFit: "cover", display: "block" }}
									/>
									<Typography
										variant="caption"
										sx={{
											display: "block",
											px: 0.75,
											py: 0.5,
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
										title={file.name.replace(/^PAY_BY_LINK-/, "")}>
										{file.name.replace(/^PAY_BY_LINK-/, "")}
									</Typography>
								</Paper>
							))}
						</Box>
					</Box>
				)}

				<Box>
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, gap: 1 }}>
						<Typography variant="subtitle2" fontWeight={600}>
							Extracted data
						</Typography>
						{rowCount > 0 && (
							<Button
								size="small"
								onClick={handleToggleAll}
								disabled={approving}
								sx={{ textTransform: "none", fontWeight: 600 }}>
								{allSelected ? "Deselect all" : "Select all"}
							</Button>
						)}
					</Box>
					<TableContainer
						component={Paper}
						elevation={0}
						sx={{
							borderRadius: 1.5,
							border: "1px solid",
							borderColor: "divider",
							maxHeight: 360,
						}}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell padding="checkbox" sx={{ bgcolor: "grey.50" }}>
										<Checkbox
											indeterminate={someSelected}
											checked={allSelected}
											onChange={handleToggleAll}
											disabled={approving || rowCount === 0}
											inputProps={{ "aria-label": "Select all rows" }}
										/>
									</TableCell>
									{(preview?.columns ?? PREFERRED_COLUMNS).map((column) => (
										<TableCell key={column} sx={{ fontWeight: 600, bgcolor: "grey.50" }}>
											{column.replace(/_/g, " ")}
										</TableCell>
									))}
								</TableRow>
							</TableHead>
							<TableBody>
								{preview?.data.length ? (
									preview.data.map((row, rowIndex) => {
										const isChecked = selectedIndexes.has(rowIndex);
										return (
											<TableRow
												key={`${row.source_filename ?? "row"}-${rowIndex}`}
												hover
												selected={isChecked}
												onClick={() => !approving && handleToggleRow(rowIndex)}
												sx={{ cursor: approving ? "default" : "pointer" }}>
												<TableCell padding="checkbox">
													<Checkbox
														checked={isChecked}
														disabled={approving}
														onClick={(event) => event.stopPropagation()}
														onChange={() => handleToggleRow(rowIndex)}
														inputProps={{ "aria-label": `Select row ${rowIndex + 1}` }}
													/>
												</TableCell>
												{preview.columns.map((column) => (
													<TableCell key={`${rowIndex}-${column}`}>
														{formatCellValue(column, row[column])}
													</TableCell>
												))}
											</TableRow>
										);
									})
								) : (
									<TableRow>
										<TableCell colSpan={(preview?.columns.length || PREFERRED_COLUMNS.length) + 1}>
											<Typography variant="body2" color="text.secondary">
												No extracted rows returned.
											</Typography>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} disabled={approving} sx={{ textTransform: "none", fontWeight: 600 }}>
					Cancel
				</Button>
				<Button
					variant="contained"
					disableElevation
					onClick={handleApprove}
					disabled={approving || !canApprove}
					startIcon={
						approving ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutlineRoundedIcon />
					}
					sx={{ textTransform: "none", fontWeight: 600, px: 3 }}>
					{approving
						? "Saving..."
						: selectedCount > 0
							? `Approve & save (${selectedCount})`
							: "Approve & save"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default PayByLinkPreviewDialog;
